import { Button } from "@/components/ui/button";
import { Link } from "wouter";

// Served from /public with stable filenames (not hashed @assets imports) so
// index.html can preload the right one with a static <link> that the
// browser's preload scanner finds on the very first pass over <head>,
// before any JS runs. Multiple widths exist so mobile visitors (the
// majority of traffic) don't download the same 1600px-wide desktop image --
// this is the actual full-bleed hero photo, so `sizes="100vw"` is accurate.
// If the photo changes, regenerate all widths with the same filenames (see
// hero-bg-{640,960,1280,1600}.{jpg,webp} in client/public), or update this
// file and the matching <link rel="preload"> in client/index.html together.
const heroImage = "/hero-bg-1280.jpg";
const heroSrcSetJpg = "/hero-bg-640.jpg 640w, /hero-bg-960.jpg 960w, /hero-bg-1280.jpg 1280w, /hero-bg-1600.jpg 1600w";
const heroSrcSetWebp = "/hero-bg-640.webp 640w, /hero-bg-960.webp 960w, /hero-bg-1280.webp 1280w, /hero-bg-1600.webp 1600w";
const heroSizes = "100vw";


export default function Hero() {
  return (
    <section className="relative min-h-[calc(100svh-4rem)] flex items-start sm:items-center overflow-hidden pt-[116px] pb-8 sm:pt-[132px] sm:pb-10">
      <div className="absolute inset-0 z-0">
        <picture>
          <source srcSet={heroSrcSetWebp} sizes={heroSizes} type="image/webp" />
          <img
            src={heroImage}
            srcSet={heroSrcSetJpg}
            sizes={heroSizes}
            alt="Effortless Gym premium facility with custom Wolverson rig and professional equipment"
            className="w-full h-full object-cover"
            width={1600}
            height={1067}
            // @ts-ignore -- fetchpriority is valid HTML but not yet in React's JSX types
            fetchpriority="high"
            data-testid="img-hero-background"
          />
        </picture>
        <div className="absolute inset-0 photo-hero-scrim"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10 w-full">
        <div className="max-w-4xl flex flex-col">
          <div className="order-0 mb-2 sm:mb-5">
            <span 
              className="inline-block text-primary text-xs sm:text-sm font-medium tracking-widest uppercase"
              data-testid="text-launch-banner"
            >
              Now Open • Bournemouth &amp; Poole
            </span>
          </div>
          
          <h1 className="order-1 text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl font-alata font-bold mb-3 sm:mb-5 leading-[1.08] sm:leading-tight tracking-tight">
            A Gym You Can Have To Yourself
          </h1>
          
          <div className="order-2 mb-4 sm:mb-6 max-w-3xl">
            <p className="text-base sm:text-lg md:text-xl text-foreground leading-relaxed font-medium">
              Book the whole gym to yourself, or train in a deliberately low-capacity shared session.
            </p>
          </div>

          <div className="order-3 sm:order-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link href="/first-session" className="w-full sm:w-auto">
              <Button
                variant="default"
                className="w-full px-6 sm:px-8 py-3 text-base sm:text-lg font-medium"
                size="lg"
              data-testid="button-book-gym-to-yourself-hero"
              >
                Book Your £14.99 First Session
              </Button>
            </Link>
            <Link href="/membership" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full px-6 sm:px-8 py-3 text-base sm:text-lg font-medium border-primary text-primary hover:bg-primary/10" size="lg" data-testid="button-view-memberships-hero">
                View Memberships
              </Button>
            </Link>
          </div>

          <div className="order-4 sm:order-5 flex flex-wrap gap-2 mt-4 mb-5 sm:mt-5 sm:mb-7">
            <span className="inline-flex items-center gap-2 bg-card/80 border border-border px-3 py-1.5 rounded-full text-xs sm:text-sm text-foreground">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              No crowds
            </span>
            <span className="inline-flex items-center gap-2 bg-card/80 border border-border px-3 py-1.5 rounded-full text-xs sm:text-sm text-foreground">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              No waiting for equipment
            </span>
            <span className="inline-flex items-center gap-2 bg-card/80 border border-border px-3 py-1.5 rounded-full text-xs sm:text-sm text-foreground">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              No feeling watched
            </span>
          </div>

        </div>
      </div>
    </section>
  );
}
